import { ApiError } from "@anna-money/anna-web-lib/api";
import { AnnaError } from "@anna-money/anna-web-lib/utils";
import { makeAutoObservable } from "mobx";

import { type Authenticator } from "auth/authenticator";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type StripeIntegrationClient } from "services/stripeIntegration/stripeIntegrationClient";
import { type PaymentMethod, type PaymentMethodResponseData } from "services/stripeIntegration/stripeIntegrationTypes";

interface PaymentMethodsLoadedState {
  paymentMethods: PaymentMethod[];
}

type PaymentMethodsState = "loading" | "error" | PaymentMethodsLoadedState;

export class PaymentMethodsStore {
  private readonly _stripeIntegrationClient: StripeIntegrationClient;
  private readonly _authenticator: Authenticator;
  private readonly _analyticsManager: AnalyticsManager;

  private _state: PaymentMethodsState = "loading";

  constructor(
    stripeIntegrationClient: StripeIntegrationClient,
    authenticator: Authenticator,
    analyticsManager: AnalyticsManager,
  ) {
    this._stripeIntegrationClient = stripeIntegrationClient;
    this._authenticator = authenticator;
    this._analyticsManager = analyticsManager;

    makeAutoObservable(this);
  }

  get state(): PaymentMethodsState {
    return this._state;
  }

  private set state(value: PaymentMethodsState) {
    this._state = value;
  }

  get hasActivePaymentMethod(): boolean {
    if (this.state === "loading" || this.state === "error") {
      return false;
    }

    return this.state.paymentMethods.some((item) => item.isActive);
  }

  async init(): Promise<void> {
    this.state = "loading";

    try {
      const companyId = this._authenticator.getCompanyId();

      if (!companyId) {
        this._analyticsManager.event("payment-methods.init-skipped", {
          reason: "missing_company_id",
        });

        this.state = {
          paymentMethods: [],
        };
        return;
      }

      const paymentMethodsData = await this._getPaymentMethods();

      this.state = {
        paymentMethods: paymentMethodsData?.paymentMethods || [],
      };
    } catch (e) {
      this.state = "error";
      throw e;
    }
  }

  private async _getPaymentMethods(): Promise<PaymentMethodResponseData | undefined> {
    try {
      return await this._stripeIntegrationClient.getPaymentMethods();
    } catch (e) {
      if (e instanceof ApiError) {
        // stripe-integration could answer with this status if we don't have a company_id yet
        if (e.diagnostics.httpCode === 400) {
          return;
        }
      }

      console.error(new AnnaError("Couldn't load payment methods", { cause: e }));

      return undefined;
    }
  }
}
